import { Table, TableBody, TableCell, TableCellProps, TableContainer, TableRow, Typography } from '@mui/material'
import React, { ReactNode } from 'react'

interface FactsTableRowProps {
    align?: TableCellProps['align']
    children?: ReactNode
    hasDivider?: boolean
    hasBottomPadding?: boolean
    IconComponent?: ReactNode
    label: ReactNode
    labelAlign?: TableCellProps['align']
    value?: ReactNode
    valueAlign?: TableCellProps['align']
    padding?: string | number
    leftColumnWidth?: string | number | null
    rightColumnWidth?: string | number | null
}

export function FactsTableRow({
    label,
    IconComponent = null,
    value = null,
    align,
    labelAlign,
    valueAlign,
    children,
    hasDivider = true,
    hasBottomPadding = true,
    padding,
    leftColumnWidth = '50%',
    rightColumnWidth = '50%',
}: FactsTableRowProps) {
    align = align || 'left'
    labelAlign = labelAlign || align
    valueAlign = valueAlign || align
    const content = value || children

    return (
        <TableRow
            sx={{
                '&:last-child td, &:last-child th': {
                    border: 0,
                },
            }}
        >
            {label && (
                <TableCell
                    align={labelAlign}
                    colSpan={content ? undefined : 10}
                    sx={{
                        ...(leftColumnWidth ? { width: leftColumnWidth } : {}),
                        borderBottom: hasDivider ? undefined : 'none',
                        paddingBottom: hasBottomPadding ? '16px' : '0',
                        padding,
                    }}
                >
                    {label} {IconComponent}
                </TableCell>
            )}
            {content && (
                <TableCell
                    align={valueAlign}
                    sx={{
                        ...(rightColumnWidth ? { width: rightColumnWidth } : {}),
                        borderBottom: hasDivider ? undefined : 'none',
                        paddingBottom: hasBottomPadding ? '16px' : '0',
                        padding,
                    }}
                >
                    {content}
                </TableCell>
            )}
        </TableRow>
    )
}

export function FactsTableRowFourColumns({
    label1,
    value1 = null,
    label2,
    value2 = null,
    align,
    labelAlign,
    valueAlign,
}) {
    align = align || 'left'
    labelAlign = labelAlign || align
    valueAlign = valueAlign || align
    return (
        <TableRow key={label1} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell align={labelAlign} sx={{ width: '25%' }}>
                {label1}
            </TableCell>
            <TableCell align={valueAlign} sx={{ width: '25%' }}>
                {value1}
            </TableCell>
            <TableCell align={labelAlign} sx={{ width: '25%' }}>
                {label2}
            </TableCell>
            <TableCell align={valueAlign} sx={{ width: '25%' }}>
                {value2}
            </TableCell>
        </TableRow>
    )
}

interface FactsTableProps {
    isSlim?: boolean
    title?: string
    rows?: ReactNode
    children?: ReactNode
    noBorder?: boolean
}

export function FactsTable({ noBorder, rows, children, isSlim, title }: FactsTableProps) {
    const content = rows || children

    return (
        <>
            {title && (
                <Typography mt={4} variant='subtitle2'>
                    {title}
                </Typography>
            )}
            <TableContainer>
                <Table sx={{ width: isSlim ? 'fit-content' : undefined, td: { border: noBorder ? 0 : undefined } }}>
                    <TableBody>{content}</TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

interface FactsTableRowFlexibleColumnsProps<T> {
    align?: TableCellProps['align']
    columns: ({ id: string } & T)[]
    firstColumnValue: ReactNode
    getColumnValue?: (v: T) => ReactNode
    IconComponent?: ReactNode
    lastColumnValue?: null | (() => ReactNode)
    labelAlign?: TableCellProps['align']
    valueAlign?: TableCellProps['align']
    valign?: TableCellProps['valign']
}

export function FactsTableRowFlexibleColumns<T>({
    columns,
    firstColumnValue,
    getColumnValue,
    lastColumnValue,
    align,
    labelAlign,
    valueAlign,
    IconComponent = null,
    valign,
}: FactsTableRowFlexibleColumnsProps<T>) {
    align = align || 'left'
    labelAlign = labelAlign || align
    valueAlign = valueAlign || align

    return (
        <TableRow key={firstColumnValue} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell align={labelAlign} width={100 / (columns.length + 1) + '%'}>
                {firstColumnValue} {IconComponent}
            </TableCell>

            {columns.map((column) => {
                return (
                    <TableCell key={column.id} align={valueAlign} sx={{ verticalAlign: valign }}>
                        {getColumnValue ? getColumnValue(column) : null}
                    </TableCell>
                )
            })}

            {lastColumnValue && (
                <TableCell align={valueAlign} sx={{ verticalAlign: valign }}>
                    {lastColumnValue()}
                </TableCell>
            )}
        </TableRow>
    )
}
